import Handlebars from 'handlebars';

import * as withdrawServices from '../withdraw-services.js';

import * as paymentMethodData from './payment-method-data.js';
import { fetchJson } from '../../core/ajax-json.js';
import { spinnerOff, siblingSpinnerOnAction } from '../../flow/flow-view.js';
import * as validation from './validation.js';
import * as validators from './validators.js';

function getContextBasedId(contextPath, id) {
	return `${contextPath}-${id}`;
}
function getMethodAccountContextId(contextPath) {
	return getContextBasedId(contextPath, 'method-account-context');
}
function getMethodAccountSelectorId(contextPath) {
	return getContextBasedId(contextPath, 'payment-method-account');
}
function getMethodSelectorId(contextPath) {
	return getContextBasedId(contextPath, 'method-selector');
}
function getAccountSelectorId(contextPath) {
	return getContextBasedId(contextPath, 'account-selector');
}

function getMethodFormElementId(contextPath) {
	return `${contextPath}-method-form`;
}
// Account for Credit Card method
function fetchCreditCardFormView(contextPath, creditCards) {
	const url = '/template/withdraw/methods/credit-card';
	return fetchJson(url)
		.then(restResult => {
			const rootContext = {
				cards: creditCards.accounts,
				i18n: restResult.result.rootContext.i18n,
				'account-selector-id': getAccountSelectorId(contextPath),
			};
			const accountTemplate = Handlebars.compile(restResult.result.templates['credit-card-selector']);
			return accountTemplate(rootContext);
		});
}
// Account for Bank Wire Withdraw, such as DotPay Payment IQ (DOTPAY_PIQ)
function fetchBankWireWithdrawFormView() {
	const url = '/template/withdraw/methods/bank-wire-withdraw';
	return fetchJson(url)
		.then(restResult => {
			const accountTemplate = Handlebars.compile(restResult.result.templates['bank-wire-withdraw']);
			return accountTemplate(restResult.result.rootContext);
		});
}

function mergeMethodWithFormTemplate(methodData) {
	methodData.paymentMethods.map(method => {
		const formTemplateType = methodData.formTemplateTypes.find(tpl => {
			return tpl.formTemplateCode === method.formTemplateCode;
		});
		method.formTemplateUrl = formTemplateType.formTemplateMobileUrl;
		return method;
	});

	return methodData;
}
function fetchWithdrawMethodFormView(contextPath, methodData, paymentMethodStore) {
	const paymentMethodSelectorId = getMethodSelectorId(contextPath);
	const paymentMethodFormId = getMethodFormElementId(contextPath);

	const lastMethod = paymentMethodStore.getMethod();
	const lastUsedMethodId = lastMethod && lastMethod.methodId;

	return fetchJson('/template/withdraw/methods')
		.then(restResult => {
			const tpls = restResult.result.templates;
			methodData = mergeMethodWithFormTemplate(methodData);
			const rootContext = {
				methods: methodData.paymentMethods,
				playerCurrency: methodData.playerCurrency,
				'payment-method-selector-id': paymentMethodSelectorId,
				'payment-method-form-id': paymentMethodFormId,
				'payment-method-account-context': getMethodAccountContextId(contextPath),
				i18n: restResult.result.rootContext.i18n,
			};
			Handlebars.registerPartial('payment-method-selector', tpls['payment-method-selector']);
			Handlebars.registerHelper('pre-select-default-method-option', (contentContext) => {
				const methodId = Handlebars.escapeExpression(contentContext.methodId);
				const methodText = Handlebars.escapeExpression(contentContext.methodText);
				const executeUrl = Handlebars.escapeExpression(contentContext.executeUrl);
				const sendHandlerType = Handlebars.escapeExpression(contentContext.sendHandlerType);
				const formTemplateCode = Handlebars.escapeExpression(contentContext.formTemplateCode);
				const formTemplateUrl = Handlebars.escapeExpression(contentContext.formTemplateUrl);
				const formTemplateUrlParams = Handlebars.escapeExpression(contentContext.formTemplateUrlParams);
				const logoImageClasses = Handlebars.escapeExpression(contentContext.logoImageClasses);
				const extraParameters = Handlebars.escapeExpression(contentContext.extraParameters);
				const selected = methodId === lastUsedMethodId ? ' selected' : '';

				return new Handlebars.SafeString(
					`<option value="${methodId}"
					class="payment__option-method"
					data-execute-url="${executeUrl}"
					data-send-handler-type="${sendHandlerType}"
					data-form-template-code="${formTemplateCode}"
					data-form-template-url="${formTemplateUrl}"
					data-form-template-url-params="${formTemplateUrlParams}"
					data-logo-image-classes="${logoImageClasses}"
					data-extra-parameters="${extraParameters}" ${selected}>${methodText}</option>`
				);
			});

			const methodTpl = Handlebars.compile(tpls['withdraw-methods']);

			const withdrawMethod = {
				html: methodTpl(rootContext),
				title: restResult.result.rootContext.i18n.MOBILE_WITHDRAW_METHOD_PAGE_TITLE,
			};
			return withdrawMethod;
		});
}

function noAccountForSelectedMethod(accountContextElement) {
	accountContextElement.innerHTML = '';
}

const formValidation = {
	forms: {
		'CREDIT-CARD': {
			fieldValidators: {
				/* Currently only implemented for Poland
				 *		”clearingNumber” field is used, while it will actually contain BIC
				 */
				card: (cardId) => {
					return { inRange: parseInt(cardId, 10) > 0 };
				},
			},
			formHandler(sharedPaymentMethodStore, formElement) {
				const validationResult = validation.validateFormElements(formElement, this.fieldValidators);

				if (validationResult.ok) {
					const cardSelect = validationResult.pass.card;
					const opt = cardSelect.options[cardSelect.selectedIndex];
					sharedPaymentMethodStore.setAccount(opt.dataset);
				} else {
					sharedPaymentMethodStore.setAccount(undefined);
				}
				return validationResult;
			},
		},
		'BANK-WIRE-WITHDRAW': {
			fieldValidators: {
				/* Currently only implemented for Poland
				 *		”clearingNumber” field is used, while it will actually contain BIC
				 */
				clearingNumber: (value) => {
					const clearingPattern = /^[A-Z0-9]{8,11}$/;
					return clearingPattern.test(value);
				},
				accountNumber: (value) => {
					return validators.onlyDigits(value);
				},
			},
			formHandler(sharedPaymentMethodStore, formElement) {
				const validationResult = validation.validateFormElements(formElement, this.fieldValidators);

				if (validationResult.ok) {
					const COUNTRY_PL = '174';
					// country code for PL...
					const accountInfo = {
						country: COUNTRY_PL
					};

					Object.keys(validationResult.pass).forEach((elementName) => {
						accountInfo[elementName] = validationResult.pass[elementName].value;
					});
					sharedPaymentMethodStore.setAccount(accountInfo);
				} else {
					sharedPaymentMethodStore.setAccount(undefined);
				}
				return validationResult;
			},
		},
	},
	onSubmit(sharedPaymentMethodStore, formElement, formTemplateType) {
		const formTemplate = this.forms[formTemplateType];
		if (formTemplate && formTemplate.formHandler) {
			return formTemplate.formHandler(sharedPaymentMethodStore, formElement);
		}
		return { ok: true };
	}
};

function accountFormHandler(sharedPaymentMethodStore, methodSelectorId, accountFormElement) {
	const methodSelector = accountFormElement.ownerDocument.getElementById(methodSelectorId);
	let formTemplateCode = undefined;
	if (methodSelector) {
		formTemplateCode = methodSelector.options[methodSelector.selectedIndex].dataset.formTemplateCode;
	}
	if (formValidation.forms[formTemplateCode] && formValidation.forms[formTemplateCode].formHandler) {
		const formTemplate = formValidation.forms[formTemplateCode];
		formTemplate.formHandler(sharedPaymentMethodStore, accountFormElement);
	}
}

function showAccountTemplateForSelectedMethod(methodOption, accountContextElement, sharedPaymentMethodStore, contextPath) {
	return withdrawServices.fetchTemplateDataForMethod(methodOption.dataset.formTemplateUrl, methodOption.dataset.formTemplateUrlParams)
		.then(accountData => {
			if (methodOption.dataset.formTemplateCode === 'CREDIT-CARD') {
				return fetchCreditCardFormView(contextPath, accountData)
					.then(accountView => {
						accountContextElement.innerHTML = accountView;
						const accountSelector = accountContextElement.ownerDocument.getElementById(getAccountSelectorId(contextPath));
						if (accountSelector) {
							accountSelector.addEventListener('change', (event) => {
								const el = event.target;
								const accountOption = el.options[el.selectedIndex];
								sharedPaymentMethodStore.setAccount(accountOption && accountOption.dataset);
							});
							const evt = new Event('change', { bubbles: false });
							accountSelector.dispatchEvent(evt);
						}
					});
			}
			if (methodOption.dataset.formTemplateCode === 'BANK-WIRE-WITHDRAW') {
				return fetchBankWireWithdrawFormView()
					.then(accountView => {
						accountContextElement.innerHTML = accountView;
					});
			}
			return undefined;
		});

}

function addMethodChangeHandler(contextPath, contextElement, sharedPaymentMethodStore) {
	const methodSelector = contextElement.ownerDocument.getElementById(getMethodSelectorId(contextPath));

	if (methodSelector === null) {
		return undefined;
	}
	methodSelector.addEventListener('change', (event) => {
		const actionButton = document.getElementById('flow-click-to-action');
		const spinner = siblingSpinnerOnAction(event.target);

		const accountFormId = getMethodAccountContextId(contextPath);
		const accountForm = contextElement.ownerDocument.getElementById(accountFormId);
		const select = event.target;
		const opt = select.options[select.selectedIndex];

		const paymentMethod = paymentMethodData.paymentMethodFactory({
			methodId: opt.value,
			methodText: opt.text,
			executeUrl: opt.dataset.executeUrl,
			formTemplateCode: opt.dataset.formTemplateCode,
			formTemplateUrl: opt.dataset.formTemplateUrl,
			formTemplateUrlParams: opt.dataset.formTemplateUrlParams,
			logoImageClasses: opt.dataset.logoImageClasses,
			extraParameters: opt.dataset.extraParameters,
			aid: null,
			isMobileDevice: true
		});

		actionButton.setAttribute('disabled', 'disabled');
		sharedPaymentMethodStore.setMethod(paymentMethod);

		if (!opt.dataset.formTemplateUrl) {
			noAccountForSelectedMethod(accountForm);
			spinnerOff(spinner);
			actionButton.removeAttribute('disabled');

		} else {
			showAccountTemplateForSelectedMethod(opt, accountForm, sharedPaymentMethodStore, contextPath)
				.then(() => {
					spinnerOff(spinner);
					actionButton.removeAttribute('disabled');
				});
		}
	});
	const evt = new Event('change', { bubbles: true });
	methodSelector.dispatchEvent(evt);
}
function addActionButtonHandler(viewDone, contextElement, sharedPaymentMethodStore) {
	const actionButton = document.getElementById('flow-click-to-action');
	if (actionButton) {
		actionButton.addEventListener('click', (event) => {
			const button = event.target;
			button.classList.add('btn--is-loading');
			const spinner = siblingSpinnerOnAction(button);

			const methodSelectorId = getMethodSelectorId(contextElement);
			const accountFormId = getMethodAccountContextId(contextElement);
			const methodSelector = button.ownerDocument.getElementById(methodSelectorId);
			const accountForm = button.ownerDocument.getElementById(accountFormId);

			const methodOption = methodSelector.options[methodSelector.selectedIndex];
			const formTemplateType = methodOption.dataset.formTemplateCode;

			const validationResult = formValidation.onSubmit(sharedPaymentMethodStore, accountForm, formTemplateType);
			if (validationResult.ok) {
				viewDone({ status: 'next' });
			} else {
				spinnerOff(spinner);
			}
		});
	}
}
function addAccountInputHandler(contextElement, accountFormId, methodSelectorId, sharedPaymentMethodStore) {
	const accountForm = contextElement.ownerDocument.getElementById(accountFormId);
	if (accountForm) {
		const handler = accountFormHandler.bind(null, sharedPaymentMethodStore, methodSelectorId, accountForm);
		const delayHandler = validation.getDelayedFormInputHandler(handler);
		accountForm.addEventListener('input', delayHandler);
	}
}
function viewReady(viewDone, contextElement, contextPath, sharedPaymentMethodStore) {
	addMethodChangeHandler(contextPath, contextElement, sharedPaymentMethodStore);
	addAccountInputHandler(contextElement, getMethodAccountContextId(contextPath), getMethodSelectorId(contextPath), sharedPaymentMethodStore);
	addActionButtonHandler(viewDone, contextPath, sharedPaymentMethodStore);
}

function withdrawMethodView(contextPath, sharedPaymentMethodStore, viewReadyPromise) {
	let addHandlers;
	const viewDonePromise = new Promise(viewDoneResolver => {
		addHandlers = viewReady.bind(null, viewDoneResolver);
	});
	viewReadyPromise.then(contextElement => {
		addHandlers(contextElement, contextPath, sharedPaymentMethodStore);
		return viewDonePromise;
	});

	return withdrawServices.fetchWithdrawPaymentMethods()
		.then(methodData => {
			// console.info('withdraw methods', methodData);
			sharedPaymentMethodStore.setCurrency(methodData.lastUsedMethodDetails.lastCurrency);
			return fetchWithdrawMethodFormView(contextPath, methodData, sharedPaymentMethodStore);
		})
		.then(withdrawMethod => {
			withdrawMethod.viewDonePromise = viewDonePromise;
			return withdrawMethod;
		});

}

export {
	withdrawMethodView,
	getMethodFormElementId,
	getMethodSelectorId,
	getMethodAccountSelectorId,
	getMethodAccountContextId,
	fetchCreditCardFormView
};



// WEBPACK FOOTER //
// ./mobile-core/src/payment/withdraw/withdraw-method.js